// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { API_TEAM_LEAD_CAMPAIGNS, API_TEAM_LEAD_GROUPS } from '@/service/url';
import { AGENT_STATES, rosterCounts } from './teamModel';
import { Dot, EmptyNote, HAIRLINE, Panel, TableShell, Td, Th, nf } from './teamUi';

const POLL_MS = 60000;

const CAMPAIGN_STATUS = {
  0: { label: 'Ready', evt: 'dial' },
  1: { label: 'Running', evt: 'live' },
  8: { label: 'Pausing', evt: 'hold' },
  9: { label: 'Paused', evt: 'hold' },
  21: { label: 'DND Processing', evt: 'wrap' },
  22: { label: 'Waiting for Channel', evt: 'wrap' },
};

const DIALER_TYPE_LABEL = { 1: 'Click2Call', 2: 'Auto Dialer', 3: 'Predictive Dialer', 4: 'Power Dialer' };

const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Polls one team-lead list endpoint while `enabled`; keeps last-known rows on failure. */
export function useTeamLeadList(url, enabled) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const resp = await getApiCall(url, {}, false);
      setRows(Array.isArray(resp) ? resp : []);
    } catch {
      /* leave the last-known rows in place */
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  return { rows, loading, refresh };
}

// Live state -> rosterCounts() key.
const LIVE_COUNTS = [['onCall', 'onCall'], ['available', 'available'], ['break', 'onBreak'], ['offline', 'offline']];

function Stat({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-semibold tabular-nums text-foreground">{typeof value === 'string' ? value : nf(value)}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

export function TeamLedGroups({ groups, loading, liveGroups = [] }) {
  const liveById = useMemo(() => new Map(liveGroups.map((g) => [String(g.id), g])), [liveGroups]);

  if (loading) return <Panel className="p-4"><EmptyNote>Loading your groups…</EmptyNote></Panel>;
  if (!groups.length) return <Panel className="p-4"><EmptyNote>You are not team lead of any call group yet.</EmptyNote></Panel>;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {groups.map((g) => {
        const liveGroup = liveById.get(String(g.id));
        const agents = liveGroup?.agents || [];
        const live = rosterCounts(agents);
        return (
          <Panel key={g.id} className="flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{g.group_name || `Group #${g.id}`}</div>
                <div className="text-[11px] text-muted-foreground">
                  ID {g.id}{g.strategy ? ` · ${titleCase(g.strategy)}` : ''}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Stat label="Members" value={g.total_members} />
              <Stat label="Campaigns" value={g.campaigns} />
            </div>

            {/* Live row only once the SSE roster carries this group (it needs Agents read). */}
            {liveGroup && (
            <div className={`flex flex-wrap gap-x-3 gap-y-1 border-t pt-2 text-[11px] ${HAIRLINE}`}>
              {LIVE_COUNTS.map(([key, countKey]) => (
                <span key={key} className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Dot tone={AGENT_STATES[key].tone} />
                  {AGENT_STATES[key].label}
                  <span className="font-semibold tabular-nums text-foreground">{live[countKey]}</span>
                </span>
              ))}
            </div>
            )}

            {agents.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {agents.map((a) => (
                  <span
                    key={a.id}
                    title={a.state?.label}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] text-foreground ${HAIRLINE}`}
                  >
                    <Dot tone={a.state?.tone || 'off'} dim={a.state?.dim} />
                    {a.name}
                  </span>
                ))}
              </div>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

export function TeamCampaigns({ campaigns, loading }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter(
      (c) =>
        String(c.campaign_name || '').toLowerCase().includes(q) ||
        String(c.campaign_id).includes(q) ||
        (c.agents || []).some((a) => String(a.name || '').toLowerCase().includes(q)),
    );
  }, [campaigns, search]);

  if (loading) return <Panel className="p-4"><EmptyNote>Loading team campaigns…</EmptyNote></Panel>;
  if (!campaigns.length) return <Panel className="p-4"><EmptyNote>None of your team's agents are on an active campaign.</EmptyNote></Panel>;

  return (
    <Panel className="flex flex-col gap-2 p-3">
      {campaigns.length > 5 && (
        <div className={`flex items-center gap-1.5 rounded-lg border ${HAIRLINE} bg-muted/20 px-2.5 py-1.5`}>
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaign, ID or agent…"
            className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
      <TableShell maxH="60vh">
        <thead>
          <tr>
            <Th>Campaign</Th>
            <Th>ID</Th>
            <Th>Status</Th>
            <Th>Type</Th>
            <Th>Agents</Th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => {
            const status = CAMPAIGN_STATUS[Number(c.campaign_status)] || { label: '—', evt: 'ended' };
            const agents = [...(c.agents || [])].sort((a, b) => String(a.name).localeCompare(String(b.name)));
            return (
              <tr key={c.campaign_id}>
                <Td className="font-semibold">{c.campaign_name || `Campaign #${c.campaign_id}`}</Td>
                <Td className="tabular-nums text-muted-foreground">{c.campaign_id}</Td>
                <Td>
                  <span className={`call-evt call-evt--static call-evt--${status.evt}`}>{status.label}</span>
                </Td>
                <Td className="text-muted-foreground">{DIALER_TYPE_LABEL[c.campaign_type] || '—'}</Td>
                <Td className="min-w-[240px] !whitespace-normal">
                  <div className="flex flex-wrap gap-1">
                    {agents.map((a) => (
                      <span
                        key={a.id}
                        title={a.active ? 'Active on this campaign' : 'Agent turned this campaign off'}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${HAIRLINE} ${a.active ? 'text-foreground' : 'text-muted-foreground line-through'}`}
                      >
                        <Dot tone={a.active ? 'good' : 'off'} />
                        {a.name}
                      </span>
                    ))}
                  </div>
                </Td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">No campaigns match</td>
            </tr>
          )}
        </tbody>
      </TableShell>
    </Panel>
  );
}
